import { getI18nLocale } from "@/plugins/i18n";
import { bracketHandler } from "@/utils/helper.js";
import { isPublicRoute } from "@/router";

const axios = require("axios");

const REFRESH_TOKEN_URL = "account/jwt/refreshToken";

// AccountApiModel is a computed that returns `new AccountApi(...)`, so a fresh
// client and interceptor exist on every access. This state is module level so all
// of them share it: one refresh in flight at a time, one teardown at most.
let refreshRequest = null;
let isSessionEnding = false;

// The refresh token can no longer recover the session, so drop it and land on the
// login page. "controlled=true" marks the landing as deliberate, which is what
// stops App.vue from bouncing us to the pay domain and straight back here.
function redirectToLogin() {
  if (isSessionEnding) {
    return;
  }
  isSessionEnding = true;

  localStorage.removeItem("auth_token");
  localStorage.removeItem("refresh_token");

  const [, locale] = window.location.pathname.split("/");
  window.location.href = `/${locale || "sv"}?controlled=true`;
}

// Nothing left to refresh with: hand over to the pay domain so it clears the
// shared login state before sending us back.
function logoutThroughPayDomain() {
  if (isSessionEnding) {
    return;
  }
  isSessionEnding = true;

  var url = new URL(window.location.href);
  var payUrl = process.env.VUE_APP_TRAINPLANET_PAY_URL;

  localStorage.clear();
  window.location.href =
    bracketHandler(payUrl) + "/sv?logout=true&came=" + url.origin;
}

export default class AccountApi {
  constructor(tenantId) {
    this.client = axios.create({
      baseURL: `${process.env.VUE_APP_TRAINPLANET_API_URL_BASE}`,
    });
    this.client.interceptors.request.use((request) => {
      request.headers["Accept-Language"] = getI18nLocale();
      request.headers["tenantId"] = tenantId;
      request.headers["Content-Type"] = "application/json";
      request.headers["AuthorizationJwt"] =
        "Bearer " + localStorage.getItem("auth_token");

      return request;
    });

    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      async (err) => {
        // Network failures, CORS rejections and aborts carry no response to read,
        // and reading `.status` off one used to throw here.
        if (!err.response) {
          return Promise.reject(err);
        }

        const error = err.response;
        const request = err.config;

        if (request.url == REFRESH_TOKEN_URL) {
          // The refresh token is the last thing that can recover the session, so a
          // rejection leaves nothing to retry with. 5xx and network errors are
          // transient and must not drop a session that is still valid.
          if (error.status >= 400 && error.status < 500 && !isPublicRoute()) {
            redirectToLogin();
          }

          return Promise.reject(error);
        }

        // A 401 is expected on a page that renders without a session, so leave it
        // to the caller rather than refreshing or moving the user.
        if (error.status != 401 || isPublicRoute()) {
          return Promise.reject(error);
        }

        if (!localStorage.getItem("refresh_token")) {
          logoutThroughPayDomain();
          return Promise.reject(error);
        }

        // Retry each request once. Before this flag a 401 that survived the
        // refresh reloaded the whole page, which re-issued the same request with
        // the same stale token and reloaded again - the loop being fixed here.
        if (request.retriedAfterRefresh) {
          redirectToLogin();
          return Promise.reject(error);
        }
        request.retriedAfterRefresh = true;

        if (!(await this.refreshOnce())) {
          return Promise.reject(error);
        }

        return this.client(request);
      }
    );
  }

  // Collapses concurrent 401s into a single refresh call. One call per failed
  // request races them against each other, and because the backend rotates the
  // refresh token the first response retires the token the rest are still using.
  refreshOnce() {
    if (!refreshRequest) {
      refreshRequest = this.refreshToken({
        refreshToken: localStorage.getItem("refresh_token"),
      }).finally(() => {
        refreshRequest = null;
      });
    }

    return refreshRequest;
  }

  getAccount() {
    return this.client.get("account/jwt");
  }

  deleteAccount() {
    // A 401 is refreshed and retried by the interceptor, so this only has to hand
    // the failure to the caller instead of swallowing it into an undefined result.
    return this.client.delete(`account/jwt`);
  }

  // Resolves with true when a usable access token was stored, false otherwise.
  // Never rejects: callers only need to know whether the next request can carry a
  // token, and the interceptor above owns the redirect when it cannot.
  refreshToken(data) {
    if (!data || !data.refreshToken) {
      return Promise.resolve(false);
    }

    return this.client
      .post(REFRESH_TOKEN_URL, data)
      .then(({ data: tokens }) => {
        if (!tokens || !tokens.accessToken) {
          return false;
        }

        localStorage.setItem("auth_token", tokens.accessToken);

        // A rotated refresh token has to replace the stored one, otherwise the
        // next refresh presents a token the backend has already retired.
        if (tokens.refreshToken) {
          localStorage.setItem("refresh_token", tokens.refreshToken);
        }

        return true;
      })
      .catch(() => false);
  }

  registerPassenger(data, force) {
    return this.client.post(`account/jwt/passenger`, data, {
      params: { force },
    });
  }

  updatePassenger(id, data) {
    return this.client.patch(`account/jwt/passenger/${id}`, data);
  }

  updateAccount(data) {
    return this.client.patch(`account/jwt/update`, data);
  }

  async getOrders(filter) {
    return await this.client.get("account/jwt/orders", {
      params: {
        ...filter,
      },
    });
  }

  getPassenger(id) {
    return this.client.get(`account/jwt/passenger?passengerId=${id}`);
  }

  getPassengerList(params) {
    return this.client.get(`account/jwt/passenger/list`, { params: params });
  }

  deletePassenger(id) {
    return this.client.delete(`account/jwt/passenger/${id}`);
  }

  getAllAddresses(params) {
    return this.client.get(`account/jwt/address`, { params: params });
  }

  getAddress(addressId) {
    return this.client.get(`account/jwt/address/${addressId}`);
  }

  createAddress(data) {
    return this.client.post(`account/jwt/address`, data);
  }

  deleteAddress(addressId) {
    return this.client.delete(`account/jwt/address/${addressId}`);
  }

  updateAddress(addressId, data) {
    return this.client.patch(`account/jwt/address/${addressId}`, data);
  }

  logout() {
    return this.client.post("account/jwt/logout");
  }

  getCompanyEmployees(companyId, params = {}) {
    return this.client
      .get(`company/jwt/${companyId}/employees`, { params })
      .then(({ data }) => data);
  }

  deleteCompanyEmployee(customerId, companyId) {
    return this.client
      .delete(`company/jwt/${companyId}/employees/${customerId}`)
      .then(({ data }) => data);
  }

  upsertCompanyEmployee(companyId, payload) {
    return this.client
      .post(`company/jwt/${companyId}/employees`, payload)
      .then(({ data }) => data);
  }
}
